/**
 * Contractor Magic Link Authentication Page
 * 
 * Handles magic link authentication for contractors.
 * URL: /contractor/magic/:token
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ContractorMagicLinkPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error, expired
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) {
      verifyMagicLink();
    }
  }, [token]);

  const verifyMagicLink = async () => {
    try {
      // First try the JWT magic link endpoint (from email notifications)
      let response;
      try {
        response = await axios.post(`${API_URL}/api/auth/contractor/jwt-magic-login`, {
          token: token
        });
      } catch (jwtError) {
        // If JWT endpoint fails, try the stored magic link endpoint
        if (jwtError.response?.status === 401) {
          response = await axios.post(`${API_URL}/api/auth/contractor/magic-login`, {
            token: token
          });
        } else {
          throw jwtError;
        }
      }

      if (response.data.success) {
        // Store the token and user info
        localStorage.setItem('contractor_portal_token', response.data.token);
        localStorage.setItem('contractor_portal_user', JSON.stringify(response.data.user));
        localStorage.setItem('contractor_company_id', response.data.user.contractor_company_id);
        localStorage.setItem('contractor_company_name', response.data.user.contractor_company_name);
        
        setStatus('success');
        toast.success('Welcome to the Contractor Portal!');
        
        // Redirect to job if specified, otherwise contractor dashboard
        const redirectTo = response.data.redirect_to || '/contractor';
        setTimeout(() => {
          navigate(redirectTo, { replace: true });
        }, 1500);
      }
    } catch (err) {
      console.error('Magic link error:', err);
      const message = err.response?.data?.detail || 'Invalid or expired access link';
      
      if (message.includes('expired')) {
        setStatus('expired');
      } else {
        setStatus('error');
      }
      setError(message);
    }
  };

  const handleRequestNewLink = async () => {
    navigate('/contractor');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white">Park Home Support</h1>
          <p className="text-teal-400 text-sm mt-1">Contractor Portal</p>
        </div>

        {/* Status Card */}
        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-8 text-center">
          
          {/* Verifying */}
          {status === 'verifying' && (
            <>
              <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
              <h2 className="text-xl font-semibold text-white mb-2">Verifying Access</h2>
              <p className="text-slate-400">Please wait while we verify your access link...</p>
            </>
          )}

          {/* Success */}
          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Access Verified!</h2>
              <p className="text-slate-400">Redirecting to your dashboard...</p>
            </>
          )}

          {/* Expired */}
          {status === 'expired' && (
            <>
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Link Expired</h2>
              <p className="text-slate-400 mb-6">
                This access link has expired. Please request a new one from the contractor portal.
              </p>
              <button
                onClick={handleRequestNewLink}
                className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium transition-colors"
              >
                Request New Access Link
              </button>
            </>
          )}

          {/* Error */}
          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Access Denied</h2>
              <p className="text-slate-400 mb-6">{error}</p>
              <button
                onClick={handleRequestNewLink}
                className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium transition-colors"
              >
                Go to Contractor Portal
              </button>
            </>
          )}
        </div>

        {/* Help Text */}
        <p className="text-center text-slate-500 text-sm mt-6">
          Need help? Contact{' '}
          <a href="mailto:support@parkhomesupport.co.uk" className="text-teal-400 hover:underline">
            support@parkhomesupport.co.uk
          </a>
        </p>
      </div>
    </div>
  );
}
